import React, { useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";

import Layout from "@/components/Layout";
import Splash from "@/components/Splash";
import SeoHead from "@/components/SeoHead";
import ModuleGuard from "@/components/ModuleGuard";
import { PlayerProvider } from "@/context/PlayerContext";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { ModulesProvider } from "@/context/ModulesContext";
import { WebSocketProvider } from "@/context/WebSocketContext";
import { CollectionProvider } from "@/context/CollectionContext";
import { DiscographyOverridesProvider } from "@/context/DiscographyOverridesContext";
import ScrollToTop from "@/components/ScrollToTop";

// Home is the landing page — keep it eager so first paint is instant.
import Home from "@/pages/Home";

// All other pages lazy-loaded for code-splitting (huge bundle reduction).
const Biography      = lazy(() => import("@/pages/Biography"));
const Discography    = lazy(() => import("@/pages/Discography"));
const Albums         = lazy(() => import("@/pages/Albums"));
const Singles        = lazy(() => import("@/pages/Singles"));
const Videos         = lazy(() => import("@/pages/Videos"));
const Gallery        = lazy(() => import("@/pages/Gallery"));
const Timeline       = lazy(() => import("@/pages/Timeline"));
const TourHistory    = lazy(() => import("@/pages/TourHistory"));
const News           = lazy(() => import("@/pages/News"));
const NewsArticle    = lazy(() => import("@/pages/NewsArticle"));
const Awards         = lazy(() => import("@/pages/Awards"));
const MediaArchive   = lazy(() => import("@/pages/MediaArchive"));
const RemixArchive   = lazy(() => import("@/pages/RemixArchive"));
const FanZone        = lazy(() => import("@/pages/FanZone"));
const Contact        = lazy(() => import("@/pages/Contact"));

const Login          = lazy(() => import("@/pages/Login"));
const Register       = lazy(() => import("@/pages/Register"));
const Profile        = lazy(() => import("@/pages/Profile"));
const PublicProfile  = lazy(() => import("@/pages/PublicProfile"));
const Forum          = lazy(() => import("@/pages/Forum"));
const ForumTopic     = lazy(() => import("@/pages/ForumTopic"));
const FanWall        = lazy(() => import("@/pages/FanWall"));
const Radio          = lazy(() => import("@/pages/Radio"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Chat           = lazy(() => import("@/pages/Chat"));
const FanMap         = lazy(() => import("@/pages/FanMap"));
const Quiz           = lazy(() => import("@/pages/Quiz"));
const WallpaperCreator = lazy(() => import("@/pages/WallpaperCreator"));
const Collections    = lazy(() => import("@/pages/Collections"));
const MyCollection   = lazy(() => import("@/pages/MyCollection"));
const Leaderboard    = lazy(() => import("@/pages/Leaderboard"));
const Messages       = lazy(() => import("@/pages/Messages"));
const Marketplace    = lazy(() => import("@/pages/Marketplace"));
const Groups         = lazy(() => import("@/pages/Groups"));
const GroupDetail    = lazy(() => import("@/pages/GroupDetail"));
const GroupTopic     = lazy(() => import("@/pages/GroupTopic"));
const Wiki           = lazy(() => import("@/pages/Wiki"));
const WikiArticle    = lazy(() => import("@/pages/WikiArticle"));
const TourMap        = lazy(() => import("@/pages/TourMap"));
const ConcertDetail  = lazy(() => import("@/pages/ConcertDetail"));
const PerformanceArchive = lazy(() => import("@/pages/PerformanceArchive"));
const CommunityHub   = lazy(() => import("@/pages/CommunityHub"));
const MediaHub       = lazy(() => import("@/pages/MediaHub"));
const ShopHub        = lazy(() => import("@/pages/ShopHub"));
const ActivityFeed   = lazy(() => import("@/pages/ActivityFeed"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const NotificationSettings = lazy(() => import("@/pages/NotificationSettings"));
const GamesHub       = lazy(() => import("@/pages/GamesHub"));
const BlueWorldAdventure = lazy(() => import("@/pages/BlueWorldAdventure"));
const PenaltyChallenge = lazy(() => import("@/pages/PenaltyChallenge"));
const ConcertManager = lazy(() => import("@/pages/ConcertManager"));
const RescueMission = lazy(() => import("@/pages/RescueMission"));
const ZorotlCharacter = lazy(() => import("@/pages/ZorotlCharacter"));
const Tickets        = lazy(() => import("@/pages/Tickets"));
const Events         = lazy(() => import("@/pages/Events"));
const Polls          = lazy(() => import("@/pages/Polls"));

const G = (name, Component) => (
  <ModuleGuard name={name}>
    <Component />
  </ModuleGuard>
);

function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div className="w-10 h-10 rounded-full border-2 border-[#00AFFF]/30 border-t-[#00AFFF] animate-spin" aria-label="Loading"/>
    </div>
  );
}

function App() {
  // Skip splash on intra-tab re-visits (cached) — show it only ONCE per session.
  const [booting, setBooting] = useState(() => {
    try { return !sessionStorage.getItem("e65_splashed"); } catch { return true; }
  });
  const handleSplashDone = () => {
    try { sessionStorage.setItem("e65_splashed", "1"); } catch { /* ignore */ }
    setBooting(false);
  };

  // Remove the inline HTML boot-shell when splash is skipped (returning visitor).
  React.useEffect(() => {
    if (!booting) {
      const el = document.getElementById("boot-shell");
      if (el) { el.classList.add("boot-gone"); setTimeout(() => { try { el.remove(); } catch { /* ignore */ } }, 350); }
    }
  }, [booting]);

  return (
    <div className="App">
      {booting && <Splash onDone={handleSplashDone} />}
      <I18nProvider>
        <AuthProvider>
          <WebSocketProvider>
            <CollectionProvider>
            <DiscographyOverridesProvider>
            <ModulesProvider>
              <PlayerProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <SeoHead />
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                    <Route element={<Layout />}>
                      <Route path="/" element={G("home", Home)} />
                      <Route path="/biography" element={G("biography", Biography)} />
                      <Route path="/discography" element={G("discography", Discography)} />
                      <Route path="/albums" element={G("discography", Albums)} />
                      <Route path="/singles" element={G("discography", Singles)} />
                      <Route path="/videos" element={G("videos", Videos)} />
                      <Route path="/gallery" element={G("gallery", Gallery)} />
                      <Route path="/timeline" element={G("timeline", Timeline)} />
                      <Route path="/tour-history" element={G("tour", TourHistory)} />
                      <Route path="/news" element={G("news", News)} />
                      <Route path="/news/:slug" element={G("news", NewsArticle)} />
                      <Route path="/tour-map" element={G("tour", TourMap)} />
                      <Route path="/concert/:id" element={G("tour", ConcertDetail)} />
                      <Route path="/archive" element={G("tour", PerformanceArchive)} />
                      <Route path="/performance-archive" element={G("tour", PerformanceArchive)} />
                      <Route path="/community" element={G("forum", CommunityHub)} />
                      <Route path="/media" element={G("videos", MediaHub)} />
                      <Route path="/shop" element={G("marketplace", ShopHub)} />
                      <Route path="/activity" element={G("forum", ActivityFeed)} />
                      <Route path="/notifications" element={G("forum", NotificationsPage)} />
                      <Route path="/notification-settings" element={G("forum", NotificationSettings)} />
                      <Route path="/games" element={G("games", GamesHub)} />
                      <Route path="/games/blue-world" element={G("games", BlueWorldAdventure)} />
                      <Route path="/games/penalty" element={G("games", PenaltyChallenge)} />
                      <Route path="/games/concert-manager" element={G("games", ConcertManager)} />
                      <Route path="/games/rescue-mission" element={G("games", RescueMission)} />
                      <Route path="/games/character" element={G("games", ZorotlCharacter)} />
                      <Route path="/awards" element={<Awards />} />
                      <Route path="/media-archive" element={G("news", MediaArchive)} />
                      <Route path="/remix-archive" element={<RemixArchive />} />
                      <Route path="/fan-zone" element={<FanZone />} />
                      <Route path="/contact" element={G("contact", Contact)} />

                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={G("register", Register)} />
                      <Route path="/profile" element={G("profiles", Profile)} />
                      <Route path="/u/:username" element={G("profiles", PublicProfile)} />
                      <Route path="/forum" element={G("forum", Forum)} />
                      <Route path="/forum/topic/:id" element={G("forum", ForumTopic)} />
                      <Route path="/fan-wall" element={G("fan_wall", FanWall)} />
                      <Route path="/chat" element={G("chat", Chat)} />
                      <Route path="/fan-map" element={G("fan_map", FanMap)} />
                      <Route path="/quiz" element={G("quiz", Quiz)} />
                      <Route path="/wallpaper-creator" element={G("wallpapers", WallpaperCreator)} />
                      <Route path="/collections" element={G("collections", Collections)} />
                      <Route path="/my-collection" element={<MyCollection />} />
                      <Route path="/leaderboard" element={<Leaderboard />} />
                      <Route path="/messages" element={G("messages", Messages)} />
                      <Route path="/marketplace" element={G("marketplace", Marketplace)} />
                      <Route path="/groups" element={G("groups", Groups)} />
                      <Route path="/groups/:slug" element={G("groups", GroupDetail)} />
                      <Route path="/groups/:slug/topic/:topicId" element={G("groups", GroupTopic)} />
                      <Route path="/wiki" element={G("wiki", Wiki)} />
                      <Route path="/wiki/:slug" element={G("wiki", WikiArticle)} />
                      <Route path="/radio" element={G("radio", Radio)} />
                      <Route path="/tickets" element={G("tickets", Tickets)} />
                      <Route path="/tickets/:id" element={G("tickets", Tickets)} />
                      <Route path="/events" element={G("events", Events)} />
                      <Route path="/polls" element={G("polls", Polls)} />
                      <Route path="/admin" element={<AdminDashboard />} />

                      <Route path="*" element={<Home />} />
                    </Route>
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </PlayerProvider>
            </ModulesProvider>
            </DiscographyOverridesProvider>
            </CollectionProvider>
          </WebSocketProvider>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
